import Mustache from 'mustache';
import './cm-tyreselector.scss';

class Tyreselector {
  // Cascade order (field values depending on previous field selection)
  #cascade = [];
  // Tyreselector form state
  #state = {};
  #formDisabled = false;
  // Results filter state
  #filterState = {};
  // Used mainly to construct titles
  #info = {};

  /**
   * @param {HTMLElement} tsElement - Tyreselector container element
   */
  constructor(tsElement) {
    const formElement = tsElement.querySelector('.js-cm-tyreselector-form');

    // Get form fields
    const fields = Array.from(formElement.elements).filter(tag => ['select', 'textarea', 'input'].includes(tag.tagName.toLowerCase()));

    // Sort fields by cascade index [data-cascade="1"]
    fields.sort((a, b) => {
      if(Number(a.dataset.cascade) < b.dataset.cascade) {
        return -1;
      }
      if(a.dataset.cascade > b.dataset.cascade) {
        return 1;
      }
      return 0;
    });

    this.container = tsElement;
    this.form = formElement;
    this.submitButton = formElement.querySelector('[type="submit"]');
    this.resultsContainer = tsElement.querySelector('.js-cm-tyreselector-results');
    this.resultsTemplate = document.getElementById('cm-tyreselector-results-template').innerHTML;
    this.noResultsTemplate = document.getElementById('cm-tyreselector-no-results-template').innerHTML;
    this.productCardTemplate = document.getElementById('cm-tyreselector-product-card-template').innerHTML;
    this.#cascade = fields.map(fieldElement => fieldElement.name);
    this.fields = {};
    this.#info = this.container.dataset;

    // Form events
    fields.forEach(field => {
      this.#state[field.name] = field.value;
      this.fields[field.name] = field;

      field.addEventListener('change', this.onFieldChange.bind(this));
    });

    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Filter events
    document.addEventListener('click', event => {
      if(event.target.classList.contains('js-cm-tyreselector-size-btn')) {
        this.onSizeBtnClick(event.target);
      }

       if(event.target.classList.contains('js-cm-tyreselector-season-btn')) {
        this.onSeasonChange(event.target.dataset.value);
      }
    });

     document.addEventListener('keydown', event => {
       if(event.key === 'Enter' && event.target.classList.contains('js-cm-tyreselector-season-btn')) {
        this.onSeasonChange(event.target.dataset.value);
      }
    });

    document.addEventListener('change', event => {
      if(event.target.classList.contains('js-cm-tyreselector-size')) {
        this.onSizeChange(event.target.value);
      }
    });

    // Disable submit button if any field is empty
    if(Object.keys(this.#state).every(key => this.fields[key].value !== '') === false) {
      this.disableForm(true);
    }

    // If results were rendered on server side already
    const filterForm = this.resultsContainer.querySelector('.js-cm-tyreselector-results-form');
    if(filterForm) {
      // Hide filter form submit button (if js enabled, filtered on form field change event)
      this.#hideFilterSubmitBtn();

      // Update filter state
      const formData = new FormData(filterForm);
      for(let [key, value] of formData.entries()) {
        this.#filterState[key] = value;
      }
    }
  }

  /**
   * Handles tyreselector form field change
   *
   * @param {Object} event - Field change event
   */
  onFieldChange(event) {
    // Set state
    const fieldName = event.target.name;
    this.#state[fieldName] = event.target.value;

    const currentIndex = this.#cascade.indexOf(fieldName);
    const nextFieldNames = this.#cascade.slice(currentIndex + 1);

    // Save selected option label to #info
    this.#info[`${fieldName}Name`] = event.target.options[event.target.selectedIndex].text;

    if(nextFieldNames.length) {
      // Empty and disable cascading selects
      nextFieldNames.forEach(fieldName => {
        this.#state[fieldName] = '';
        const selectElement = this.fields[fieldName];
        this.emptySelect(selectElement);
        selectElement.disabled = true;
      });

      this.disableForm(true);

      // Populate next select options based on current select value
      const nextFieldName = nextFieldNames[0];
      if(event.target.value && nextFieldName) {
        const nextSelectElement = this.fields[nextFieldName];
        nextSelectElement.classList.add('loading');

        // Get next select options
        this.getData({state: this.#state, type: nextFieldName}).then(data => {
          // Populate next select options
          this.populateSelect(nextSelectElement, data);

          // Enable next select
          nextSelectElement.disabled = false;
          nextSelectElement.classList.remove('loading');
        }).catch(error => {
          console.error('Car manufacturer tyreselector', error);
        });
      }
    } else {
      // Current field was the last, enable submit btn if selected value is not empty
      if(event.target.value) {
        this.disableForm(false);
      } else {
        this.disableForm(true);
      }
    }
  }

  /**
   * Fetches fitting tyres on tyreselector form submit
   *
   * @param {object} event - Form submit event
   */
  onSubmit(event) {
    event.preventDefault();

    if(!this.#formDisabled) {

      this.toggleFormLoading(true);
      this.#updateUrlParams(this.#state);

      this.getData({state: {...this.#state, ...this.#info}, type: 'products'}).then(data => {
        /*
        data = {
          sizes: [],
          families: [],
          seasons: [],
          title: '',
        }
        */

        // 1. Enable form
        this.toggleFormLoading(false);

        // 2. Update filter state (size + season)
        const selectedSizeId = data.sizes.find(size => size.selected)?.id;
        const selectedSeasonId = data.seasons.find(season => season.selected)?.id;
        this.#filterState.size = selectedSizeId || '';
        this.#filterState.season = selectedSeasonId || '';

        // 3. Render result filters and products
        if(data.sizes.length) {
          this.resultsContainer.innerHTML = Mustache.render(this.resultsTemplate, data);
        } else {
          this.resultsContainer.innerHTML = Mustache.render(this.noResultsTemplate, data);
        }

        // 4. Hide filter form submit button (if js enabled, filtered on form field change event)
        this.#hideFilterSubmitBtn();

        // 5. Set focus to first focusable element
        this.#setFocusToFirstFocusableChild(this.resultsContainer);

        // 6. Scroll to results
        this.#scrollTo(this.resultsContainer);
      }).catch(error => {
        console.error('Car manufacturer tyreselector products', error);
      });
    }
  }

  /**
   * When js is enabled, hide filter submit button,
   * since filtering happens on field change via ajax.
   * Without js the submit button submits the form and filtering happens serverside.
   */
  #hideFilterSubmitBtn() {
    const submitBtn = this.resultsContainer.querySelector('.js-cm-tyreselector-results-filter');
    if(submitBtn) {
      submitBtn.style.display = 'none';
    }
  }

  /**
   * Toggles form loading state
   * Functionality is disabled during loading
   *
   * @param {boolean} state - Loading or not
   */
  toggleFormLoading(state) {
    this.form.classList.toggle('loading', state);
    this.disableForm(state);
  }

  /**
   * Enables or disables the tyreselector form
   *
   * @param {boolean} state - Disabled state
   */
  disableForm(state) {
    // Functional
    this.#formDisabled = state;
    // Visual
    this.submitButton.disabled = state;
  }

  /**
   * Removes options from select, except the first empty one that is used as a placeholder
   *
   * @param {HTMLElement} selectElement - <select>
   */
  emptySelect(selectElement) {
    selectElement.querySelectorAll('option').forEach(option => {
      if(option.value !== '') {
        option.remove();
      }
    });
  }

  /**
   * Populates <select> with options from array
   *
   * @param {HTMLElement} selectElement - <select>
   * @param {array} options - Array of options
   *  @param {string} options.id - Option id
   *  @param {string} options.name - Option name
   */
  populateSelect(selectElement, options) {
    let markup = '';

    options.forEach(option => {
      markup += `<option value="${option.id}">${option.name}</option>`;
    });

    selectElement.insertAdjacentHTML('beforeend', markup);
  }

  /**
   * Sets focus to first focusable and visible child
   *
   * @param {HTMLElement} parentEl - Parent element
   */
  #setFocusToFirstFocusableChild(parentEl) {
    const focusable = Array.from(parentEl.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(el =>
      window.getComputedStyle(el).getPropertyValue('display') !== 'none'
    );

    if(focusable[0]) {
      focusable[0].focus();
    }
  }

  /**
   * Scrolls to element
   *
   * @param {HTMLElement} element - Target element
   */
  #scrollTo(element) {
    const nav = document.querySelector('.navigation');
    const navHeight = nav?.getBoundingClientRect().height || 0;
    const y = element.getBoundingClientRect().top + window.scrollY - (navHeight * 2);
    window.scroll({
      top: y,
      behavior: 'smooth',
    });
  }

  #updateUrlParams(newParams) {
    const searchParams = new URLSearchParams(window.location.search);

    Object.entries(newParams).forEach(([key, value]) => searchParams.set(key, value));

    window.history.replaceState(null, '', `?${searchParams.toString()}`);
  }

  // FILTERING

  /**
   * Updates size select value when button is clicked.
   * Buttons visible in desktop, select in mobile.
   *
   * @param {HTMLElement} btn - Size button element
   */
  onSizeBtnClick(btn) {
    // Just sync selected size to the actual select element
    const select = this.container.querySelector('.js-cm-tyreselector-size');
    if(select) {
      select.value = btn.dataset.value;
      this.onSizeChange(select.value);
    }
  }

  /**
   * Tyre size change event handler
   *
   * @param {string} newValue - New size value
   */
  onSizeChange(newValue) {
    // 1. Update state
    this.#filterState.size = newValue;

    // 2. Sync selected size to buttons in desktop
    this.container.querySelectorAll('.js-cm-tyreselector-size-btn').forEach(btn => {
      if (btn.dataset.value === newValue) btn.setAttribute('aria-selected', 'true');
      if (btn.dataset.value !== newValue) btn.setAttribute('aria-selected', 'false');
    });

    // 3. Filter results
    this.filterResults();
  }


  /**
   * Season change event handler
   *
   * @param {string} newValue - New season value
   */
  onSeasonChange(newValue) {
    this.#filterState.season = newValue;

    // 2. Sync selected size to buttons in desktop
    this.container.querySelectorAll('.js-cm-tyreselector-season-btn').forEach(btn => {
      if (btn.dataset.value === newValue) btn.setAttribute('aria-selected', 'true');
      if (btn.dataset.value !== newValue) btn.setAttribute('aria-selected', 'false');
    });

    this.filterResults();
  }

  /**
   * Filters products based on filter form values (currently size & season)
   */
  filterResults() {
    const listEl = this.resultsContainer.querySelector('.js-cm-tyreselector-results-list');
    listEl.classList.add('loading');

    this.#updateUrlParams(this.#filterState);

    this.getData({state: this.#filterState, type: 'filter'}).then(data => {
      // 1. Update season counts
      this.resultsContainer.querySelectorAll('.js-cm-tyreselector-season-count').forEach(countEl => {
        const id = countEl.dataset.season;
        const count = data.seasonCounts[id] || 0;

        countEl.innerText = count;
      });

      // 2. Reset loading state
      listEl.classList.remove('loading');

      // 3. Update season to title, if exists
      const titleSeason = this.resultsContainer.querySelector('.js-cm-tyreselector-season-name');
      if(titleSeason) {
        titleSeason.innerText = data.seasonName;
      }

      // 4. Render tyre list
      const markup = data.products.length > 0 ? data.products.map(product => Mustache.render(this.productCardTemplate, product)).join('') : Mustache.render(this.noResultsTemplate);
      listEl.innerHTML = markup;
    }).catch(error => {
      console.error('Car manufacturer tyreselector filter', error);
    });
  }

  // DATA FETCHING

  /**
   * Logging api request info in development only
   *
   * @param {object} response - Request response
   * @param {string} title - Log title
   *
   * @returns {object}
   */
  #logResponse = (response, title) => {
    if(process.env.NODE_ENV === 'development') {
      console.log(title || 'API response', response);
    }

    return response;
  };

  /**
   * Fetches data for populating cascading tyreselector fields
   *
   * @param {object} params - Query params
   *  @param {object} params.state - Current form or filter state
   *  @param {string} params.type - Type of data to fetch
   *
   * @returns {Promise}
   */
  getData(params) {

    const queryParams = {
      'stage-language': document.documentElement.lang,
      type: params.type,
      ...params.state,
    };

    return fetch('/stage-ajax/car-manufacturers?' + new URLSearchParams(queryParams))
      .then(response => this.#logResponse(response, 'API raw response'))
      .then(response => {
        if(!response.ok) {
          const error = new Error(`${response.status} ${response.statusText}`);
          error.response = response;
          error.type = 'httpError';
          // Try to parse the response body as JSON.
          return response.json().then(
            (body) => {
              // Add body property to error
              error.body = body;
              // Reject the new promise
              throw error;
            },
            () => {
              // Reject the new promise
              throw error;
            }
          );
        } else {
          return response;
        }
      })
      .then(response => (response.status === 204 ? {} : response.json()))
      .then(response => this.#logResponse(response, 'API parsed response'));
  }
}

/**
 * Initialises functionality
 *
 * @param {HTMLElement} tyreselectorElement - Tyre selector container element
 */
export function init(tyreselectorElement) {
  try {
    console.log('init cm-tyreselector');
    new Tyreselector(tyreselectorElement);
  } catch(e) {
    console.error('cm-tyreselector', e);
  }
}
